import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  PageContainer,
  Button,
  Input,
  FormField,
  GstinInput,
  RadioGroup,
  Select,
  ErrorBoundary,
  useToast,
} from '@bbpos/ui';
import { locationService } from '@/services/location.service';
import { useKeyboardShortcut } from '@/hooks/use-keyboard-shortcut';
import { useTaxRegime } from '@/hooks/use-tax-regime';
import { STATE_OPTIONS } from '@/constants/indian-states';
import { PHONE_RE, LOCATION_CODE_RE, generateLocationCode } from '@/utils/validation';
import type { CreateLocationPayload, LocationCatalogueMode } from '@/types/location';
import { useTenantDial } from '@/hooks/use-tenant-dial';
import { useTenantCountry } from '@/hooks/use-tenant-country';
import { addressFieldsFor, clampPostalInput, isValidPostalCode } from '@/utils/address-fields';
import { LocationTimezoneSelect } from '@/components/features/locations/LocationTimezoneSelect';

// ─── Design tokens ──────────────────────────────────────────────────────────
const GLASS_PANEL =
  'rounded-2xl bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm shadow-[0_12px_30px_-18px_rgba(15,23,42,0.22)] dark:shadow-[0_12px_30px_-18px_rgba(0,0,0,0.5)]';

function cn(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(' ');
}

// What the new store starts selling (master catalogue plan D4).
export const CATALOGUE_OPTIONS: { value: LocationCatalogueMode; label: string; description: string }[] = [
  { value: 'EMPTY', label: 'Start empty', description: 'Choose its products later.' },
  { value: 'ALL_ACTIVE', label: 'Add all active products', description: 'Every product sold at any store, with stock 0.' },
  { value: 'COPY_FROM', label: 'Copy from a store', description: 'The products another store sells, with stock 0.' },
];

/** The `catalogue` part of the create payload; nothing for "Start empty", the API default. */
export function cataloguePayload(
  mode: LocationCatalogueMode,
  sourceLocationId: string,
): CreateLocationPayload['catalogue'] {
  if (mode === 'EMPTY') return undefined;
  if (mode === 'COPY_FROM') return { mode, source_location_id: sourceLocationId };
  return { mode };
}

// ─── Content ────────────────────────────────────────────────────────────────

function LocationCreateContent() {
  const dial = useTenantDial();
  const country = useTenantCountry();
  const addressFields = addressFieldsFor(country, STATE_OPTIONS);
  const navigate = useNavigate();
  const { toast } = useToast();
  const tax = useTaxRegime();

  const [name, setName] = useState('');
  const [locationCode, setLocationCode] = useState('');
  const [codeManuallyEdited, setCodeManuallyEdited] = useState(false);
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [pincode, setPincode] = useState('');
  const [phone, setPhone] = useState('');
  const [gstin, setGstin] = useState('');
  const [timezone, setTimezone] = useState<string | null>(null);
  const [catalogueMode, setCatalogueMode] = useState<LocationCatalogueMode>('EMPTY');
  const [sourceLocationId, setSourceLocationId] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: locData } = useQuery({
    queryKey: ['locations', 'all'],
    queryFn: () => locationService.list({ limit: 100 }),
    staleTime: 60_000,
  });
  const sourceOptions = (locData?.data ?? [])
    .filter((l) => l.is_active)
    .map((l) => ({ value: l.id, label: l.location_code ? `${l.name} (${l.location_code})` : l.name }));

  const createMutation = useMutation({
    mutationFn: (payload: CreateLocationPayload) => locationService.create(payload),
    onSuccess: (location) => {
      toast({ title: 'Location created successfully', variant: 'success' });
      navigate(`/locations/${location.id}`);
    },
    onError: (err: Error & { code?: string }) => {
      if (err.message?.includes('already exists') || err.message?.includes('DUPLICATE') || err.code === 'LOCATION_CODE_DUPLICATE') {
        setErrors((prev) => ({ ...prev, location_code: 'Location code already exists' }));
      } else {
        toast({ title: err.message ?? 'Failed to create location', variant: 'error' });
      }
    },
  });

  function handleNameChange(value: string) {
    setName(value);
    if (!codeManuallyEdited) {
      setLocationCode(generateLocationCode(value));
    }
  }

  function handleCodeChange(value: string) {
    setCodeManuallyEdited(true);
    setLocationCode(value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10));
  }

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!name || name.length < 2) {
      errs.name = 'Location name is required (min 2 characters)';
    }
    if (!locationCode || !LOCATION_CODE_RE.test(locationCode)) {
      errs.location_code = 'Location code is required (alphanumeric only)';
    } else if (locationCode.length > 10) {
      errs.location_code = 'Location code must be 10 characters or fewer';
    }
    if (!isValidPostalCode(pincode, country.iso)) {
      errs.pincode = `${addressFields.postalLabel} is not valid for ${country.name}`;
    }
    if (phone && !PHONE_RE.test(phone)) {
      errs.phone = 'Enter a valid phone number';
    }
    if (gstin && tax.taxIdRegex && !tax.taxIdRegex.test(gstin)) {
      errs.gstin = `Invalid ${tax.taxIdLabel} format`;
    }
    if (catalogueMode === 'COPY_FROM' && !sourceLocationId) {
      errs.source_location_id = 'Choose the store to copy from';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleSubmit() {
    if (!validate()) return;
    createMutation.mutate({
      name: name.trim(),
      location_code: locationCode,
      address: address || undefined,
      city: city || undefined,
      state: state || undefined,
      pincode: pincode || undefined,
      phone: phone || undefined,
      gstin: gstin || undefined,
      timezone: timezone ?? undefined,
      catalogue: cataloguePayload(catalogueMode, sourceLocationId),
    });
  }

  // Ctrl+Enter → Create (spec 3.11)
  useKeyboardShortcut('Enter', useCallback(() => {
    handleSubmit();
  }, [name, locationCode, address, city, state, pincode, phone, gstin, timezone, catalogueMode, sourceLocationId]));

  return (
    <div className="max-w-2xl mx-auto animate-fade-in">
      <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
        <div className={cn(GLASS_PANEL, 'p-6 animate-slide-up')}>
          {/* Section: Location Information */}
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400 dark:text-neutral-500 mb-4">
            Location Information
          </h3>

          <div className="space-y-4">
            <FormField label="Location Name" htmlFor="loc-name" required error={errors.name}>
              <Input
                id="loc-name"
                placeholder="e.g., Main Store"
                value={name}
                onChange={(v) => handleNameChange(v)}
              />
            </FormField>

            <FormField label="Location Code" htmlFor="loc-code" required error={errors.location_code}>
              <Input
                id="loc-code"
                placeholder="e.g., MG01"
                value={locationCode}
                onChange={(v) => handleCodeChange(v)}
              />
              <p className="mt-1 text-xs text-neutral-400 dark:text-neutral-500">
                Used in bill numbers. Auto-generated from name, but you can edit it.
              </p>
            </FormField>

            <FormField label="Address" htmlFor="loc-address">
              <Input
                id="loc-address"
                placeholder="e.g., 123 MG Road"
                value={address}
                onChange={(v) => setAddress(v)}
              />
            </FormField>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="City" htmlFor="loc-city">
                <Input
                  id="loc-city"
                  placeholder={`e.g., ${addressFields.cityPlaceholder}`}
                  value={city}
                  onChange={(v) => setCity(v)}
                />
              </FormField>

              <FormField label={addressFields.regionLabel} htmlFor="loc-state">
                <Select
                  id="loc-state"
                  options={addressFields.regionOptions}
                  value={state}
                  onChange={(v) => setState(typeof v === 'string' ? v : '')}
                  placeholder={addressFields.regionPlaceholder}
                />
              </FormField>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label={addressFields.postalLabel} htmlFor="loc-pincode" error={errors.pincode}>
                <Input
                  id="loc-pincode"
                  placeholder={addressFields.postalPlaceholder ? `e.g., ${addressFields.postalPlaceholder}` : ''}
                  value={pincode}
                  onChange={(v) => setPincode(clampPostalInput(v, addressFields))}
                />
              </FormField>

              <FormField label="Phone" htmlFor="loc-phone" error={errors.phone}>
                <Input
                  id="loc-phone"
                  variant="phone"
                  defaultDial={dial}
                  placeholder="98765 43210"
                  value={phone}
                  onChange={(v) => setPhone(v)}
                />
              </FormField>
            </div>

            <LocationTimezoneSelect id="loc-timezone" value={timezone} onChange={setTimezone} />
          </div>

          {/* Section: Tax & Compliance */}
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400 dark:text-neutral-500 mt-8 mb-4">
            Tax & Compliance
          </h3>

          <div className="space-y-4">
            <GstinInput
              regime={tax}
              value={gstin}
              onChange={setGstin}
              error={errors.gstin}
            />
          </div>

          {/* Section: Products */}
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400 dark:text-neutral-500 mt-8 mb-4">
            Products
          </h3>

          <div className="space-y-4">
            <RadioGroup
              label="What should this store sell?"
              variant="card"
              options={CATALOGUE_OPTIONS}
              value={catalogueMode}
              onChange={(v) => setCatalogueMode(v as LocationCatalogueMode)}
            />
            {catalogueMode === 'COPY_FROM' && (
              <FormField label="Copy from" htmlFor="loc-catalogue-source" required error={errors.source_location_id}>
                <Select
                  id="loc-catalogue-source"
                  options={sourceOptions}
                  value={sourceLocationId}
                  onChange={(v) => setSourceLocationId(typeof v === 'string' ? v : '')}
                  placeholder="Choose a store"
                />
              </FormField>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between mt-8 pt-4">
            <Button variant="ghost" onClick={() => navigate('/locations')}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              loading={createMutation.isPending}
            >
              Create Location
            </Button>
          </div>

          <p className="text-xs text-neutral-400 dark:text-neutral-500 text-center mt-2">
            Press Ctrl+Enter to submit
          </p>
        </div>
      </form>
    </div>
  );
}

export function LocationCreatePage() {
  return (
    <PageContainer
      title="New Location"
      subtitle="Add a new business location"
      variant="compact"
      breadcrumbs={[{ label: 'Locations', href: '/locations' }, { label: 'New Location' }]}
    >
      <ErrorBoundary>
        <LocationCreateContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
